/**
 * @file     hubKitStyles.ts
 * @purpose  Shared style vocabulary for the hub kit: colour refs as CSS
 *           variables (safe in Server Components and theme-correct before
 *           hydration), the eyebrow and micro-label treatments, the hairline,
 *           focus ring and motion helpers, and the figure filter every kit
 *           component uses so a dash or "Unavailable" never renders as a value.
 */
import type { SemanticColorTokens } from '@/core/types';
import { SEMANTIC_CSS_VARS, semanticRoleColors } from '@/design-system/tokens/semanticColors';
import { layoutTokens } from '@/design-system/tokens/layout';
import { motionTokens } from '@/design-system/tokens/motion';
import { fontWeightTokens, typographyTokens } from '@/design-system/tokens/typography';

const semanticVarRefs = Object.fromEntries(
  Object.entries(SEMANTIC_CSS_VARS).map(([key, cssVar]) => [key, `var(${cssVar})`]),
) as Record<keyof SemanticColorTokens, string>;

export const hubColors = {
  ...semanticVarRefs,
  ...semanticRoleColors,
  // accent[700]: the boot CSS names it the eyebrow colour; the semantic set has no key for it.
  accentText: 'var(--r-accent-strong)',
} as const;

export const hubRadius = {
  /** Buttons, inputs, search bars, segmented controls. */
  control: layoutTokens.radius.md,
  /** Cards, panels, tables, listing photos, map rows. */
  panel: layoutTokens.radius.lg,
} as const;

export const hairline = `1px solid ${hubColors.borderSubtle}`;

export const headingFont = typographyTokens.fontFamilyHeading;

// Sentence-case labels at 500: colour and size mark them, not caps and weight.
export const eyebrowSx = {
  m: 0,
  fontSize: 14,
  fontWeight: fontWeightTokens.medium,
  lineHeight: 1.4,
  letterSpacing: '-0.003em',
  textTransform: 'none',
  color: hubColors.accentText,
} as const;

export const microLabelSx = {
  m: 0,
  fontSize: 12.5,
  fontWeight: fontWeightTokens.medium,
  lineHeight: 1.4,
  letterSpacing: '0.005em',
  textTransform: 'none',
  color: hubColors.textMuted,
} as const;

export const focusRingSx = {
  '&:focus-visible': {
    outline: `2px solid ${hubColors.interactiveFocusRing}`,
    outlineOffset: 2,
  },
} as const;

export const transition = (...properties: string[]) =>
  properties
    .map((p) => `${p} ${motionTokens.durations.fast}ms ${motionTokens.easing.standard}`)
    .join(', ');

export const reducedMotionSx = {
  '@media (prefers-reduced-motion: reduce)': { transition: 'none', animation: 'none' },
} as const;

const UNAVAILABLE = new Set(['', '-', '–', '—', '--', 'n/a', 'na', 'unavailable', 'not available', 'null', 'undefined', 'nan']);

/**
 * True when a figure is worth a tile. Null, empty, non-finite and every
 * stand-in for "no data" (a dash, "Unavailable", "N/A") are dropped, so the
 * kit never prints a placeholder where a number belongs.
 */
export function isDisplayableFigure(value: unknown): value is string | number {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  if (UNAVAILABLE.has(normalized)) return false;
  return !/\bnan\b|undefined|null/.test(normalized);
}
